import "./QnaCreateForm.scss";

import { Alert, Button, Flex, Skeleton, Text } from "@fluentui/react-northstar";
import FormState, { FormData } from "@shopify/react-form-state";
import { SurveyLimits } from "@teams/cards";
import { randomString } from "@teams/client-shared";
import { AuthoringFormTrackingFields, OpenEndedQuestion, TeamsSurveyDraft } from "@teams/types";
import { SurveyDTO } from "@teams/types/api";
import React, { useEffect } from "react";

import { AuthoringFormHeader } from "../../..../../../SurveyAuthoring/AuthoringFormHeader";
import { useTeamsDetails } from "../../../../../_shared/actions/useTeamsDetails";
import { useUserInfo } from "../../../../../_shared/actions/useUserInfo";
import { CheckboxField } from "../../../../../_shared/components/fields";
import { TextAreaField } from "../../../../../_shared/components/fields/TextAreaField";
import { FetchError, FetchResult, isError } from "../../../../../_shared/services/ChannelApi";
import { surveyApi } from "../../../../../_shared/services/SurveyApi";
import { useTeamsContext } from "../../../../../_shared/state/TeamsContextProvider";
import { getAudienceFromTeamsContext } from "../../../../../_shared/utils/Authoring";
import { clientEventTracker } from "../../../../../_shared/utils/clientTelemetry";
import { InstallWarning } from "../../../components/InstallWarning";
import { useTrackingProperties } from "../../../SurveyAuthoring/PollyAuthoring/hooks/useTrackingProperties";
import { AuthoringFormHeaderOptions } from "../../../SurveyAuthoring/PollySurveyAuthoringForm";

type Props = {
  onCreated: (survey: SurveyDTO) => void;
  editSurvey?: SurveyDTO;
  trackingFields?: AuthoringFormTrackingFields;
  headerOptions?:
    | {
        showHeader?: false;
      }
    | ({
        showHeader: true;
      } & AuthoringFormHeaderOptions);
};

export const QnaCreateForm: React.FC<Props> = props => {
  const { onCreated, editSurvey, headerOptions, trackingFields } = props;
  const { inActiveMeeting, groupType, hostClientType, meetingType } = useTrackingProperties({
    isPersonal: trackingFields?.isPersonal ?? false,
  });

  const { teamsContext } = useTeamsContext();
  const { user } = useUserInfo();

  const { isTeamsDetailsLoading, loadTeamsDetails } = useTeamsDetails();

  useEffect(() => {
    loadTeamsDetails(teamsContext);
  }, [loadTeamsDetails, teamsContext]);

  const submit = async (form: FormData<FormFields>) => {
    const { fields } = form;

    const audienceItem = getAudienceFromTeamsContext(teamsContext)!;

    if (!audienceItem) {
      return [{ message: "Oops, something went wrong while creating the Q&A." }];
    }

    const question: OpenEndedQuestion = {
      id: randomString(),
      templateId: "",
      label: fields.message.value,
      questionType: "open-ended",
      required: true,
      allowComments: false,
      contentType: "text/plain",
    };
    const draft: TeamsSurveyDraft = {
      type: "qna",
      title: fields.message.value,
      requireApproval: fields.requireApproval.value,
      allowReactions: fields.allowReactions.value,
      description: "",
      anonymity: "voluntary",
      resultsVisibility: "real-time",
      commentsEnabled: false,
      deliveryPreference: "in-group",
      reminders: [],
      questions: [question],
      recipients: [
        {
          id: audienceItem.id,
          groupId: audienceItem.groupId,
          type: audienceItem.type,
          name: audienceItem.name,
        },
      ],
    };

    let result: FetchResult<{ survey: SurveyDTO }>;
    if (editSurvey?.id) {
      result = await surveyApi.updateSurvey(teamsContext, editSurvey.id, {
        ...draft,
        title: fields.message.value,
        questions: [{ ...editSurvey.questions[0], label: fields.message.value }],
        requireApproval: fields.requireApproval.value,
        allowReactions: fields.allowReactions.value,
      });
    } else {
      result = await surveyApi.createSurvey(teamsContext, draft, {
        authoringOrigin: trackingFields?.authoringOrigin,
        trackingSource: trackingFields?.trackingSource,
        groupType,
        hostClientType,
        inActiveMeeting,
        meetingType,
      });
    }

    if (isError(result)) {
      clientEventTracker.track("qna/create-form", user, {
        error: result,
      });

      if (result instanceof FetchError && result.statusCode === 409) {
        return [{ message: "You must add the Polly app to the meeting to send a Q&A" }];
      }

      return [result];
    }

    onCreated(result.survey);
  };

  if (isTeamsDetailsLoading) {
    return (
      <Skeleton animation="wave" className="polly-authoring-page">
        <Flex column gap="gap.medium">
          <Skeleton.Shape width="100%" height="100px" />
          <Skeleton.Line height="2px" />
          <Skeleton.Button width="100%" />
          <Skeleton.Button />
        </Flex>
      </Skeleton>
    );
  }

  if (!teamsContext.meeting) {
    return <InstallWarning type="qna" />;
  }

  const initialValues: FormFields = editSurvey
    ? {
        message: editSurvey.questions[0].label,
        requireApproval: editSurvey.requireApproval || false,
        allowReactions: editSurvey.allowReactions || false,
      }
    : {
        message: "Do you have any questions?",
        requireApproval: false,
        allowReactions: true,
      };

  return (
    <>
      {headerOptions?.showHeader && (
        <AuthoringFormHeader
          isEdit={headerOptions.isEdit}
          templateId={headerOptions.templateId}
          trackingSource={headerOptions.trackingSource}
        />
      )}

      <Flex column gap="gap.medium" hAlign="stretch">
        <Text content="Q&A Session" weight="bold" size="large" />

        <FormState initialValues={initialValues} onSubmit={submit}>
          {({ fields, submit, submitting, errors }) => {
            return (
              <Flex column gap="gap.medium" hAlign="stretch">
                <TextAreaField
                  field={fields.message}
                  label="Message to the attendees"
                  readOnly={submitting}
                  disabled={submitting}
                  maxLength={SurveyLimits.Questions.OpenEnded.Value.MaxLength}
                  rows={4}
                  fluid
                  resize="none"
                  autoFocus
                />

                <CheckboxField
                  field={fields.allowReactions}
                  disabled={submitting}
                  trueLabel="Allow upvoting of questions"
                  falseLabel="Allow upvoting of questions"
                  label="Question Reactions"
                  tooltip="Attendees can upvote questions."
                />

                <CheckboxField
                  field={fields.requireApproval}
                  disabled={submitting}
                  trueLabel="Review and approve questions before displaying to attendees"
                  falseLabel="Review and approve questions before displaying to attendees"
                  label="Question Approval"
                  tooltip="Moderators can review and approve the questions before displaying to the participants."
                />

                {errors?.length > 0 &&
                  errors.map(e => <Alert key={e.message} danger content={e.message} />)}
                <Flex hAlign="end" style={{ marginTop: "1rem" }} gap="gap.small">
                  <Button
                    content="Save"
                    onClick={submit}
                    loading={submitting}
                    disabled={submitting || !fields.message.value}
                    primary
                  />
                </Flex>
              </Flex>
            );
          }}
        </FormState>

        <Flex column className="qna-create-form__info" gap="gap.medium">
          <Text content="Participants can choose to submit their questions anonymously or non-anonymously." />
          <Text content="You can answer questions with an optional written response, either privately or publicly." />
        </Flex>
      </Flex>
    </>
  );
};

type FormFields = {
  message: string;
  requireApproval: boolean;
  allowReactions: boolean;
};
